/**
 * Shared visual primitives for the mentor workspace.
 *
 * Deliberately reuses the redesigned ICF Progress language (reviewDesk tokens,
 * 18px panels, pill chips) so the two surfaces read as one product.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';

export const mentorStyles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: reviewDesk.canvas },
  content: { width: '100%', maxWidth: 1240, alignSelf: 'center', padding: 28, gap: 20 },
  contentTablet: { maxWidth: 820, paddingHorizontal: 24 },
  contentPhone: { padding: 16, gap: 16 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 12 },
  header: { gap: 10 },
  title: { color: reviewDesk.ink, fontSize: 30, lineHeight: 37, fontWeight: '800' },
  titlePhone: { fontSize: 24, lineHeight: 30 },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 21 },
  panel: {
    width: '100%',
    backgroundColor: reviewDesk.card,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    borderRadius: 18,
    padding: 18,
    gap: 12,
  },
  panelTitle: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  panelSubtitle: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  bodyText: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 21 },
  mutedText: { color: reviewDesk.muted, fontSize: 13, lineHeight: 19 },
  columns: { flexDirection: 'row', alignItems: 'flex-start', gap: 20 },
  columnsStacked: { flexDirection: 'column' },
  column: { flex: 1, width: '100%', gap: 20 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  flexOne: { flex: 1 },
  fieldLabel: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  input: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: reviewDesk.ink,
    backgroundColor: reviewDesk.card,
    minHeight: 42,
  },
  inputMultiline: { minHeight: 90, textAlignVertical: 'top' },
  pressed: { opacity: 0.72 },
  errorText: { color: '#B42318', lineHeight: 21 },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
  },
  tableHeaderCell: { color: reviewDesk.muted, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4 },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
  },
  tableCell: { color: reviewDesk.inkSoft, fontSize: 13 },
});

export function Panel({ children, testID }: { children: React.ReactNode; testID?: string }) {
  return <View testID={testID} style={mentorStyles.panel}>{children}</View>;
}

export type ChipTone = 'neutral' | 'accent' | 'success' | 'warning';

const CHIP_TONES: Record<ChipTone, { bg: string; fg: string }> = {
  neutral: { bg: reviewDesk.cardMuted, fg: reviewDesk.inkSoft },
  accent: { bg: reviewDesk.accentSoft, fg: reviewDesk.accent },
  success: { bg: reviewDesk.successSoft, fg: reviewDesk.success },
  warning: { bg: reviewDesk.amberSoft, fg: '#8A5B00' },
};

export function Chip({ label, tone = 'neutral', icon }: {
  label: string;
  tone?: ChipTone;
  icon?: keyof typeof MaterialIcons.glyphMap;
}) {
  const palette = CHIP_TONES[tone];
  return (
    <View style={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: palette.bg,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
    }}>
      {icon ? <MaterialIcons name={icon} size={14} color={palette.fg} /> : null}
      <Text style={{ color: palette.fg, fontSize: 12, fontWeight: '700' }}>{label}</Text>
    </View>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // minWidth, not flexBasis: a basis is a HEIGHT when fields are stacked in a
    // column (modals), which left 180px-tall gaps around every text field.
    <View style={{ gap: 6, flexGrow: 1, minWidth: 180 }}>
      <Text style={mentorStyles.fieldLabel}>{label}</Text>
      {children}
    </View>
  );
}

export function TextField({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  keyboardType,
  editable = true,
  testID,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  keyboardType?: 'default' | 'numeric' | 'email-address';
  editable?: boolean;
  testID?: string;
}) {
  return (
    <Field label={label}>
      <TextInput
        testID={testID}
        accessibilityLabel={label}
        accessibilityState={{ disabled: !editable }}
        style={[mentorStyles.input, multiline && mentorStyles.inputMultiline, !editable && { opacity: 0.6 }]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={reviewDesk.muted}
        multiline={multiline}
        keyboardType={keyboardType}
        editable={editable}
      />
    </Field>
  );
}

export interface SegmentOption<TValue extends string | number> {
  value: TValue;
  label: string;
}

export function SegmentedField<TValue extends string | number>({
  label,
  options,
  value,
  onChange,
  disabled,
  testID,
}: {
  label?: string;
  options: SegmentOption<TValue>[];
  value: TValue | null | undefined;
  onChange: (value: TValue) => void;
  disabled?: boolean;
  testID?: string;
}) {
  const body = (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }} testID={testID}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={String(option.value)}
            testID={testID ? `${testID}_${option.value}` : undefined}
            onPress={() => !disabled && onChange(option.value)}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityState={{ selected, disabled: Boolean(disabled) }}
            aria-selected={selected}
            style={({ pressed }) => [{
              paddingVertical: 7,
              paddingHorizontal: 12,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: selected ? reviewDesk.accent : reviewDesk.line,
              backgroundColor: selected ? reviewDesk.accentSoft : reviewDesk.card,
              opacity: disabled ? 0.55 : 1,
            }, pressed && mentorStyles.pressed]}
          >
            <Text style={{
              fontSize: 12,
              fontWeight: selected ? '800' : '600',
              color: selected ? reviewDesk.accent : reviewDesk.inkSoft,
            }}>
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
  if (!label) return body;
  return <Field label={label}>{body}</Field>;
}

export function ActionButton({
  label,
  onPress,
  icon,
  variant = 'secondary',
  disabled,
  testID,
}: {
  label: string;
  onPress: () => void;
  icon?: keyof typeof MaterialIcons.glyphMap;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  testID?: string;
}) {
  const palette = variant === 'primary'
    ? { bg: reviewDesk.accent, fg: '#FFFFFF', border: reviewDesk.accent }
    : variant === 'danger'
      ? { bg: reviewDesk.card, fg: '#B42318', border: '#F0C4BE' }
      : { bg: reviewDesk.card, fg: reviewDesk.accent, border: reviewDesk.line };
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled: Boolean(disabled) }}
      style={({ pressed }) => [{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 40,
        paddingHorizontal: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: palette.border,
        backgroundColor: palette.bg,
        opacity: disabled ? 0.5 : 1,
      }, pressed && mentorStyles.pressed]}
    >
      {icon ? <MaterialIcons name={icon} size={17} color={palette.fg} /> : null}
      <Text style={{ color: palette.fg, fontSize: 13, fontWeight: '700' }}>{label}</Text>
    </Pressable>
  );
}
